'use client';

/**
 * Fires an addToCart event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useAddToCartEvent();
 * fireEvent(items);
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { getValueOrElseEmptyString } from 'utils/string';
import { getInventoryStatus } from 'utils/analytics';
import {
  ADD_TO_CART,
  CHECKFITMENT,
  DEFAULT_BRAND,
  DEFAULT_BUSINESS_UNIT
} from 'constants/analyticsConstants';
import { Availability, ItemPrice, SourcingDetail } from 'services/order/types';

type AddToCartType = {
  product: { [key: string]: unknown };
  cartId?: string;
  currencyCode?: string;
  dealerId?: string | number;
  model?: string;
  paymentMethods?: string[];
  storeName: string;
  list: string;
};

export const useAddToCartEvent = () => {
  const t = useTranslations();
  const CONTACT_YOUR_DEALER = t('CONTACT_YOUR_DEALER');

  const buildEvent = useCallback(
    ({
      cartId,
      currencyCode,
      dealerId,
      model,
      paymentMethods,
      product,
      storeName,
      list
    }: AddToCartType) => {
      const cartDetails = {
        businessUnit: DEFAULT_BUSINESS_UNIT,
        cartId: getValueOrElseEmptyString(cartId),
        dealerId: getValueOrElseEmptyString(dealerId),
        paymentMethod: (paymentMethods ?? []).join(', ')
      };

      const availability = product.availability as Availability[];
      const sourcingDetails = product.sourcingDetails as SourcingDetail[];
      const priceWithoutGST = product.priceWithoutGST as ItemPrice;
      const productName = product.name ?? product.partName;

      const addedProduct = {
        brand: DEFAULT_BRAND,
        businessUnit: DEFAULT_BUSINESS_UNIT,
        category: getValueOrElseEmptyString(
          product.category || product.categoryPath
        ),
        checkFitment: CHECKFITMENT,
        classification: getValueOrElseEmptyString(product.classification),
        id: getValueOrElseEmptyString(product.partNumber),
        inventoryLocation: getValueOrElseEmptyString(storeName),
        inventoryStatus: getValueOrElseEmptyString(
          getInventoryStatus(CONTACT_YOUR_DEALER, availability, sourcingDetails)
        ),
        kitId: getValueOrElseEmptyString(product.kitId),
        list: getValueOrElseEmptyString(list),
        msrp: getValueOrElseEmptyString(product.msrp),
        name: getValueOrElseEmptyString(productName ?? product.description),
        packageFlag: getValueOrElseEmptyString(product.packageFlag),
        position: '',
        price: getValueOrElseEmptyString(
          priceWithoutGST?.formatted?.totalPrice
        ),
        productAttributesVisible: getValueOrElseEmptyString(
          product.productAttributesVisible
        ),
        productWeight: getValueOrElseEmptyString(product.weight),
        quantity: getValueOrElseEmptyString(product.quantity),
        relatedModel: getValueOrElseEmptyString(product.model || model)
      };

      return {
        cartDetails,
        ecommerce: {
          currencyCode: getValueOrElseEmptyString(currencyCode),
          add: { products: [addedProduct] }
        },
        event: ADD_TO_CART
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fireEvent = useCallback(
    (data: AddToCartType) => {
      sendGTMEvent(buildEvent(data));
    },
    [buildEvent]
  );

  return { fireEvent };
};
